import type { ChangeView, CoverageView } from "../../api/src/shared/activity.js";
import type { DetailView, HistoryView, LocalLine } from "../data/activity.js";
import type { HistoryTarget } from "../data/history.js";
import {
  changeHeading, connectionPhrase, fieldLines, fold, groupLines, occurrenceLine, plain, refText, sentence, shownTime, snapshotLines, timeLines,
  when, type FieldLine, type Lookup, type Part, type Ref, type Row,
} from "../lib/activityWords.js";
import { el, setText } from "../lib/dom.js";
import { titled } from "../lib/environment.js";
import { icon } from "../lib/icons.js";
import { activityPath, leave, listPath, navigate, opener, parseRoute, type Route } from "../lib/router.js";
import type { Screen, Shell } from "./ui.js";

/**
 * An owner's history on a page of its own (g110): the account's from
 * Settings, a list's from List options and an item's from its menu.
 *
 * The page is built like the note page: a way back, one heading, then what
 * it has to say. Every row is a sentence ("You completed Milk.") with its
 * time, and a Details button that reads the action's whole change through
 * the store. This device's own changes that history does not hold yet are in
 * a section of their own, so nothing waiting looks accepted.
 *
 * Everything stored is drawn as text through `el()`: a name, a note or an
 * app's own label is whatever someone typed, and it is never markup and
 * never an instruction. A deleted list or item keeps its page, named from
 * history, which is the one way to see what became of it.
 */

/** Exported so the tests pin the words. */
export const ACTIVITY_WORDS = {
  title: "Activity",
  listTitle: (name: string) => `Activity for ${name}`,
  account: "Who changed your lists, and when. Lists you deleted are here too.",
  deletedList: "This list was deleted.",
  deletedItem: "This item was deleted.",
  /** After 30 days (g126) the server keeps none of what a deleted list or item held. */
  expiredList: "This list was deleted. Its name and content are no longer kept.",
  expiredItem: "This item was deleted. Its content is no longer kept.",
  back: (place: string) => `Back to ${place}`,
  loading: "Loading activity…",
  unavailable: "Activity isn't available here yet.",
  offlineEmpty: "Can't reach Trayflux. Activity loads when you're back online.",
  signedOut: "Signed out on this device. Sign in again to see activity.",
  failed: "Activity couldn't be loaded.",
  notFound: "Trayflux has no activity for this yet.",
  stale: (at: string) => `Showing activity as of ${at}. Can't reach Trayflux.`,
  staleFailed: (at: string) => `Showing activity as of ${at}. The latest couldn't be loaded.`,
  empty: { account: "No activity yet.", list: "No activity for this list yet.", item: "No activity for this item yet." },
  tryAgain: "Try again",
  older: "Show older activity",
  loadingOlder: "Loading older activity…",
  olderShown: (n: number) => `${n} older ${n === 1 ? "action" : "actions"} shown.`,
  start: (at: string) => `History starts ${at}. Nothing earlier was recorded.`,
  stopped: (from: string, to: string) => `Nothing was recorded from ${from} to ${to}.`,
  stoppedSince: (from: string) => `Nothing has been recorded since ${from}.`,
  gap: (at: string) => `Some changes made before ${at} may be missing.`,
  localTitle: "Not synced yet",
  localNote: "These changes are on this device. They join the history once Trayflux has them.",
  waiting: "Waiting to sync",
  refused: (reason?: string) => (reason ? `Not accepted (${reason})` : "Not accepted"),
  details: "Details",
  detailsFor: (text: string) => `Details: ${text}`,
  loadingDetails: "Loading details…",
  detailsOffline: "Can't reach Trayflux. Details load when you're back online.",
  detailsFailed: "These details couldn't be loaded.",
  moreChanges: "Show more changes",
  before: "Before",
  after: "After",
  history: "Activity",
  activityFor: "Activity for ",
};

/** How many lists and items a row's Details link to: a batch can name hundreds. */
const RELATED_LINKS = 5;

type ActivityRoute = Extract<Route, { name: "activity" }>;

export function activityScreen(shell: Shell, route: ActivityRoute): Screen {
  const { store } = shell;
  let listId = route.list;
  const itemId = route.item;
  const target: HistoryTarget = itemId ? { kind: "item", itemId } : listId ? { kind: "list", listId } : { kind: "account" };
  const node = el("section", { class: "screen activity" });
  const title = document.title;
  let gone = false;
  let shown: HistoryView = store.activity(target);
  /** Rows whose Details are open, by the action they read. */
  const open = new Set<string>();
  const detailStops = new Map<string, () => void>();
  /** Each row's node, kept while what it says is unchanged, so a sync never takes the focus from a Details button. */
  const drawn = new Map<string, { li: HTMLLIElement; key: string }>();
  let olderBefore: number | null = null;

  const lookup: Lookup = {
    listName: (id) => store.list(id)?.name ?? historyName("list", id),
  };

  /** A list's or an item's name as the newest history this page holds gave it. */
  function historyName(type: "list" | "item", id: string): string | null {
    for (const action of shown.actions) {
      for (const line of action.changes) if (line.entityType === type && line.entityId === id && line.label !== null) return line.label;
    }
    return null;
  }

  /** Whether the history this page holds says the server cleared this list's or item's content (g126). */
  function historyExpired(type: "list" | "item", id: string): boolean {
    return shown.actions.some((action) => action.changes.some((line) => line.entityType === type && line.entityId === id && line.expired === true));
  }

  function place(): string {
    const from = opener();
    const to = from ? parseRoute(from) : undefined;
    if (to?.name === "today") return "Today";
    if (to?.name === "upcoming") return "Upcoming";
    if (to?.name === "lists") return "Lists";
    if (to?.name === "settings") return "Settings";
    if (to?.name === "activity") return ACTIVITY_WORDS.history;
    const id = to?.name === "list" || to?.name === "note" ? to.id : listId;
    if (!to && target.kind === "account") return "Settings";
    return (id && store.list(id)?.name) || "Lists";
  }

  function fallback(): string {
    if (target.kind === "account") return "/settings";
    if (listId && store.list(listId)) return listPath(listId);
    return activityPath();
  }

  const backButton = el("button", {
    class: "activity-back",
    attrs: { type: "button" },
    children: [icon("back"), el("span")],
    on: { click: () => leave(fallback()) },
  });
  const heading = el("h1", { class: "activity-title" });
  const lede = el("p", { class: "muted activity-lede" });
  const status = el("div", { class: "activity-status" });
  const local = el("section", { class: "activity-local", attrs: { "aria-labelledby": "activity-local-title" } });
  const rows = el("ol", { class: "activity-rows", attrs: { "aria-label": ACTIVITY_WORDS.history } });
  const empty = el("p", { class: "muted activity-empty" });
  const older = el("button", {
    class: "secondary activity-older",
    attrs: { type: "button" },
    text: ACTIVITY_WORDS.older,
    on: { click: () => { olderBefore = shown.actions.length; void store.moreActivity(target); } },
  });
  const coverage = el("p", { class: "muted activity-coverage" });
  node.replaceChildren(backButton, el("div", { class: "activity-head", children: [heading, lede] }), status, local, rows, empty, older, coverage);

  /* ---------------------------------------------------------- heading */

  function name(): { text: string | null; deleted: boolean; expired: boolean } {
    if (target.kind === "account") return { text: null, deleted: false, expired: false };
    const ready = store.status() === "ready";
    if (target.kind === "list") {
      const live = store.list(target.listId);
      return { text: live?.name ?? historyName("list", target.listId), deleted: ready && !live, expired: !live && historyExpired("list", target.listId) };
    }
    const live = store.item(target.itemId);
    return { text: live?.text ?? historyName("item", target.itemId), deleted: ready && !live, expired: !live && historyExpired("item", target.itemId) };
  }

  function paintHead(): void {
    setText(backButton.lastElementChild!, ACTIVITY_WORDS.back(place()));
    const { text, deleted, expired } = name();
    if (target.kind === "account") {
      setText(heading, ACTIVITY_WORDS.title);
      setText(lede, ACTIVITY_WORDS.account);
      lede.hidden = false;
    } else {
      const fallbackName = expired ? `a deleted ${target.kind}` : target.kind === "list" ? "a list" : "an item";
      heading.replaceChildren(ACTIVITY_WORDS.listTitle(""), el("bdi", { text: text ?? fallbackName }));
      const words = target.kind === "list"
        ? (expired ? ACTIVITY_WORDS.expiredList : ACTIVITY_WORDS.deletedList)
        : (expired ? ACTIVITY_WORDS.expiredItem : ACTIVITY_WORDS.deletedItem);
      setText(lede, deleted ? words : "");
      lede.hidden = !deleted;
    }
    document.title = titled(text ? `${ACTIVITY_WORDS.title}: ${text}` : ACTIVITY_WORDS.title);
  }

  /* ---------------------------------------------------------- status */

  // One notice and one Try again, kept across repaints, so a sync never takes the focus off the button.
  const notice = el("p", { class: "activity-notice" });
  const retry = el("button", {
    class: "secondary",
    attrs: { type: "button" },
    text: ACTIVITY_WORDS.tryAgain,
    on: { click: () => { stop(); stop = store.watchActivity(target, paint); } },
  });

  function paintStatus(): void {
    const say = (text: string, again = false) => {
      setText(notice, text);
      order(status, again ? [notice, retry] : [notice]);
    };
    switch (shown.status) {
      case "unavailable": return say(ACTIVITY_WORDS.unavailable);
      case "loading": return say(ACTIVITY_WORDS.loading);
      case "offline": return say(ACTIVITY_WORDS.offlineEmpty, true);
      case "signed-out": return say(ACTIVITY_WORDS.signedOut);
      case "error": return say(ACTIVITY_WORDS.failed, true);
      case "not_found": return say(ACTIVITY_WORDS.notFound);
      default: break;
    }
    const unreachable = store.syncState().link !== "ok";
    if (shown.readAt !== null && (shown.failure || unreachable)) {
      const at = when(shown.readAt);
      return say(shown.failure && shown.failure !== "offline" ? ACTIVITY_WORDS.staleFailed(at) : ACTIVITY_WORDS.stale(at), shown.failure !== null);
    }
    order(status, []);
  }

  /** Puts the nodes in order, moving only those out of place, so a node that stays keeps its focus. */
  function order(parent: HTMLElement, nodes: readonly HTMLElement[]): void {
    nodes.forEach((child, at) => { if (parent.children[at] !== child) parent.insertBefore(child, parent.children[at] ?? null); });
    while (parent.children.length > nodes.length) parent.lastElementChild!.remove();
  }

  /* ---------------------------------------------------------- rows */

  /**
   * A list or an item named in a sentence: its name as stored, isolated so
   * its own direction never reorders the sentence. Not a link: a name in a
   * sentence is no 44px target. The row's Details hold the way to its history.
   */
  function drawPart(part: Part): Node {
    if (typeof part === "string") return document.createTextNode(part);
    if ("name" in part) return el("bdi", { class: "activity-who", text: part.name });
    return el("bdi", { class: "activity-name", text: refText(part) });
  }

  /** Every list and item a row names that has a page of its own other than this one, each once. */
  function related(row: Row): Ref[] {
    const seen = new Set<string>();
    return row.members.flatMap((member) => sentence({ action: member, members: [member], group: null }, lookup))
      .filter((part): part is Ref => typeof part !== "string" && "ref" in part && part.id !== "")
      .filter((part) => {
        const listOfItem = part.ref === "item" ? part.listId : part.id;
        const here = (part.ref === "item" && target.kind === "item" && part.id === target.itemId)
          || (part.ref === "list" && target.kind === "list" && part.id === target.listId);
        const key = `${part.ref}:${part.id}`;
        if (here || !listOfItem || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, RELATED_LINKS);
  }

  /** The way to a named list's or item's own history, a deleted one's included: a whole target, under the facts. */
  function relatedLinks(row: Row): Node[] {
    const refs = related(row);
    if (!refs.length) return [];
    return [el("ul", {
      class: "activity-related",
      children: refs.map((part) => {
        const href = part.ref === "item" ? activityPath(part.listId!, part.id) : activityPath(part.id);
        return el("li", { children: [el("a", {
          class: "activity-related-link",
          attrs: { href },
          children: [icon("history"), el("span", { children: [ACTIVITY_WORDS.activityFor, el("bdi", { text: refText(part) })] })],
          on: { click: (raw) => {
            const event = raw as MouseEvent;
            if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            navigate(href, { opener: true });
          } },
        })] });
      }),
    })];
  }

  function drawRow(row: Row): HTMLLIElement {
    const action = row.action;
    const parts = sentence(row, lookup);
    const time = shownTime(action);
    const detailId = `activity-detail-${action.id}`;
    const detail = el("div", { class: "activity-detail", attrs: { id: detailId } });
    const expanded = open.has(action.id);
    detail.hidden = !expanded;
    const toggle = el("button", {
      class: "activity-toggle",
      attrs: { type: "button", "aria-expanded": String(expanded), "aria-controls": detailId, "aria-label": ACTIVITY_WORDS.detailsFor(plain(parts)) },
      children: [el("span", { text: ACTIVITY_WORDS.details }), icon("down")],
      on: { click: () => flip(row) },
    });
    if (expanded) paintDetail(row, detail);
    return el("li", {
      class: "activity-row",
      attrs: { "data-action": action.id },
      children: [
        el("p", { class: "activity-sentence", children: parts.map(drawPart) }),
        el("div", {
          class: "activity-row-foot",
          children: [
            el("time", {
              class: "muted activity-time",
              attrs: { datetime: new Date(time.at).toISOString() },
              text: when(time.at),
            }),
            toggle,
          ],
        }),
        detail,
      ],
    });
  }

  function flip(row: Row): void {
    const id = row.action.id;
    if (open.has(id)) open.delete(id);
    else {
      open.add(id);
      if (!row.group) {
        if (!detailStops.has(id)) detailStops.set(id, store.onActivityDetail(id, () => repaintDetail(id)));
        // Every opening asks: the store skips a detail it holds or is reading, and tries again one that failed.
        void store.loadActivityDetail(id);
      }
    }
    const li = rows.querySelector<HTMLElement>(`[data-action="${CSS.escape(id)}"]`);
    if (!li) return;
    const detail = li.querySelector<HTMLElement>(".activity-detail")!;
    const toggle = li.querySelector<HTMLButtonElement>(".activity-toggle")!;
    toggle.setAttribute("aria-expanded", String(open.has(id)));
    detail.hidden = !open.has(id);
    if (open.has(id)) paintDetail(row, detail);
  }

  /**
   * Reads again the details of open rows that have none: dropped when names
   * changed, or, once the connection may be back, never reached.
   */
  function reloadOpen(retryOffline: boolean): void {
    for (const id of open) {
      if (!detailStops.has(id)) continue;
      const state = store.activityDetail(id);
      if (state === null || (retryOffline && state.status === "offline")) void store.loadActivityDetail(id);
    }
  }

  function repaintDetail(id: string): void {
    if (gone || !open.has(id)) return;
    const li = rows.querySelector<HTMLElement>(`[data-action="${CSS.escape(id)}"]`);
    const row = fold(shown.actions).find((candidate) => candidate.action.id === id);
    if (li && row) paintDetail(row, li.querySelector<HTMLElement>(".activity-detail")!);
  }

  /** The whole of one row: how it arrived, both clocks, its group, and every change, notes whole. */
  function paintDetail(row: Row, into: HTMLElement): void {
    const action = row.action;
    const facts = [connectionPhrase(action.actor, action.channel), ...timeLines(action)];
    const blocks: Node[] = [el("ul", { class: "activity-facts", children: facts.map((text) => el("li", { text })) })];
    blocks.push(...relatedLinks(row));
    if (row.group) {
      blocks.push(el("ul", { class: "activity-facts", children: groupLines(row.group).map((text) => el("li", { text })) }));
      blocks.push(el("ul", {
        class: "activity-members",
        children: row.members.filter((member) => member.kind !== "batch.items")
          .map((member) => el("li", { children: sentence({ action: member, members: [member], group: null }, lookup).map(drawPart) })),
      }));
      into.replaceChildren(...blocks);
      return;
    }
    const state = store.activityDetail(action.id);
    blocks.push(...detailBody(state));
    into.replaceChildren(...blocks);
  }

  function detailBody(state: DetailView | null): Node[] {
    if (!state || state.status === "loading") return [el("p", { class: "muted", text: ACTIVITY_WORDS.loadingDetails })];
    if (!state.view) {
      const text = state.status === "offline" ? ACTIVITY_WORDS.detailsOffline : ACTIVITY_WORDS.detailsFailed;
      return [el("p", { class: "activity-notice", text })];
    }
    const view = state.view;
    const out: Node[] = view.changes.items.map(drawChange);
    if (view.changes.next) {
      out.push(el("button", {
        class: "secondary activity-more-changes",
        attrs: { type: "button", ...(state.loadingMore ? { "aria-disabled": "true" } : {}) },
        text: ACTIVITY_WORDS.moreChanges,
        on: { click: () => { if (!state.loadingMore) void store.loadActivityDetail(view.action.id, true); } },
      }));
    }
    if (state.failure) out.push(el("p", { class: "activity-notice", text: state.failure === "offline" ? ACTIVITY_WORDS.detailsOffline : ACTIVITY_WORDS.detailsFailed }));
    return out;
  }

  function drawChange(change: ChangeView): HTMLElement {
    const lines: FieldLine[] = [...snapshotLines(change), ...fieldLines(change, lookup)];
    const occurrence = occurrenceLine(change);
    return el("div", {
      class: "activity-change",
      children: [
        el("p", { class: "activity-change-heading", children: [el("bdi", { text: changeHeading(change) })] }),
        ...(occurrence ? [el("p", { class: "muted", text: occurrence })] : []),
        ...(lines.length ? [el("dl", { class: "activity-fields", children: lines.flatMap(drawField) })] : []),
      ],
    });
  }

  function drawField(line: FieldLine): Node[] {
    const value = (text: string) => el("bdi", { class: line.block ? "activity-text" : "activity-value", text });
    if (!line.before) return [el("dt", { text: line.label }), el("dd", { children: [value(line.after)] })];
    if (line.block) {
      return [el("dt", { text: line.label }), el("dd", { children: [
        el("p", { class: "muted activity-side", text: ACTIVITY_WORDS.before }), value(line.before),
        el("p", { class: "muted activity-side", text: ACTIVITY_WORDS.after }), value(line.after),
      ] })];
    }
    return [el("dt", { text: line.label }), el("dd", { children: [value(line.before), el("span", { class: "muted", text: " → " }), value(line.after)] })];
  }

  /* ---------------------------------------------------------- local */

  function localName(line: LocalLine): string {
    const fields = line.fields as Record<string, unknown>;
    const before = line.before as { text?: string; name?: string } | null;
    if (line.entity === "list") return String(fields.name ?? before?.name ?? store.list(line.id)?.name ?? "a list");
    return String(fields.text ?? before?.text ?? store.item(line.id)?.text ?? "an item");
  }

  function localWords(line: LocalLine): string {
    const name = localName(line);
    const fields = line.fields as Record<string, unknown>;
    switch (line.op) {
      case "upsert": return line.before ? `Changed ${line.entity === "list" ? "the list " : ""}${name}` : line.entity === "list" ? `Created the list ${name}` : `Added ${name}`;
      case "patch":
        if (fields.done === true) return `Completed ${name}`;
        if (fields.done === false) return `Marked ${name} not done`;
        return `Changed ${name}`;
      case "delete": return line.entity === "list" ? `Deleted the list ${name}` : `Deleted ${name}`;
      case "move": return `Moved ${name}`;
      case "complete": return `Completed an occurrence of ${name}`;
      case "undo": return `Undid the last completion of ${name}`;
      case "import": return "Imported a list";
      default: return `Changed ${name}`;
    }
  }

  function paintLocal(): void {
    const lines = shown.local;
    local.hidden = lines.length === 0;
    if (!lines.length) { local.replaceChildren(); return; }
    local.replaceChildren(
      el("h2", { class: "activity-local-title", attrs: { id: "activity-local-title" }, text: ACTIVITY_WORDS.localTitle }),
      el("p", { class: "muted", text: ACTIVITY_WORDS.localNote }),
      el("ul", {
        class: "activity-local-rows",
        children: lines.map((line) => el("li", {
          class: "activity-row",
          attrs: { "data-state": line.state },
          children: [
            el("p", { class: "activity-sentence", children: [el("bdi", { text: `${localWords(line)}.` })] }),
            el("p", { class: "muted activity-time", text: [
              line.state === "refused" ? ACTIVITY_WORDS.refused(line.reason) : ACTIVITY_WORDS.waiting,
              ...(line.occurredAt !== null ? [when(line.occurredAt)] : []),
            ].join(" · ") }),
          ],
        })),
      }),
    );
  }

  /** A row's node: the one already drawn while its words and names are the same, or a new one. */
  function rowNode(row: Row): HTMLLIElement {
    const key = JSON.stringify([row.members, row.group, plain(sentence(row, lookup))]);
    const held = drawn.get(row.action.id);
    if (held && held.key === key) return held.li;
    const li = drawRow(row);
    drawn.set(row.action.id, { li, key });
    return li;
  }

  /** Puts the rows in order, as `order` does, and forgets the drawn rows that went. */
  function arrange(parent: HTMLElement, nodes: readonly HTMLElement[]): void {
    order(parent, nodes);
    const kept = new Set(nodes);
    for (const [id, held] of drawn) if (!kept.has(held.li)) drawn.delete(id);
  }

  /* ---------------------------------------------------------- the page */

  function paintCoverage(rowsShown: number): void {
    const start = startOf(shown.coverage);
    const lines = breaksOf(shown.coverage).map((line) =>
      line.kind === "stopped" ? ACTIVITY_WORDS.stopped(when(line.from), when(line.to))
        : line.kind === "since" ? ACTIVITY_WORDS.stoppedSince(when(line.from))
          : ACTIVITY_WORDS.gap(when(line.at)));
    // Where history began is said once the oldest row is on the page; a break in it is said straight away.
    if (start !== null && !shown.more && rowsShown > 0) lines.push(ACTIVITY_WORDS.start(when(start)));
    coverage.hidden = lines.length === 0;
    setText(coverage, lines.join(" "));
  }

  function paint(next: HistoryView): void {
    if (gone) return;
    const before = shown.actions.length;
    shown = next;
    paintHead();
    paintStatus();
    paintLocal();
    const folded = fold(shown.actions);
    const drawnBefore = new Set(rows.children);
    const olderFocused = document.activeElement === older;
    arrange(rows, folded.map(rowNode));
    rows.hidden = folded.length === 0;
    const ready = shown.status === "ready";
    empty.hidden = !ready || folded.length > 0 || shown.local.length > 0;
    setText(empty, ACTIVITY_WORDS.empty[target.kind]);
    older.hidden = !shown.more;
    setText(older, shown.loadingMore ? ACTIVITY_WORDS.loadingOlder : ACTIVITY_WORDS.older);
    older.toggleAttribute("aria-disabled", shown.loadingMore);
    paintCoverage(folded.length);
    // Show older went with the focus on it: the first row it brought takes the focus, or else the last row.
    if (olderFocused && older.hidden) {
      const row = [...rows.children].find((li) => !drawnBefore.has(li)) ?? rows.lastElementChild;
      row?.querySelector<HTMLElement>(".activity-toggle")?.focus();
    }
    reloadOpen(false);
    if (olderBefore !== null && !shown.loadingMore) {
      const added = shown.actions.length - olderBefore;
      olderBefore = null;
      if (added > 0 && before < shown.actions.length) shell.announce(ACTIVITY_WORDS.olderShown(added));
    }
  }

  let stop = store.watchActivity(target, paint);
  const stopLists = store.onLists(() => paint(shown));
  const stopItems = store.onAllItems((change) => { if (target.kind === "item" && change.id === target.itemId) paint(shown); });
  const stopSync = store.onSyncState(() => { paint(shown); reloadOpen(true); });
  paint(store.activity(target));

  return {
    node,
    update(next: Route) {
      if (next.name !== "activity" || next.item !== itemId) return;
      listId = next.list;
      paintHead();
    },
    dispose() {
      gone = true;
      stop();
      stopLists();
      stopItems();
      stopSync();
      for (const release of detailStops.values()) release();
      detailStops.clear();
      document.title = title;
    },
  };
}

/**
 * Where history begins, by the server's word: the earliest start of capture
 * it reports. Never `schema_installed`, which says only that the tables
 * existed, while nothing was yet recorded into them (g111).
 */
export function startOf(coverage: readonly CoverageView[] | null): number | null {
  const starts = (coverage ?? []).filter((row) => row.kind === "capture_started").map((row) => row.at);
  return starts.length ? Math.min(...starts) : null;
}

/** A break in history the page says: capture off for a while, off still, or a gap an operator recorded. */
export type CoverageBreak =
  | { kind: "stopped"; from: number; to: number }
  | { kind: "since"; from: number }
  | { kind: "gap"; at: number };

/** Every break after history began, newest first, as the rows above it are. */
export function breaksOf(coverage: readonly CoverageView[] | null): CoverageBreak[] {
  const rows = [...(coverage ?? [])].sort((a, b) => a.at - b.at);
  const breaks: CoverageBreak[] = [];
  let stoppedAt: number | null = null;
  let started = false;
  for (const row of rows) {
    if (row.kind === "capture_started") {
      if (stoppedAt !== null) breaks.push({ kind: "stopped", from: stoppedAt, to: row.at });
      stoppedAt = null;
      started = true;
    } else if (row.kind === "capture_stopped" && started && stoppedAt === null) {
      stoppedAt = row.at;
    } else if (row.kind === "gap" && started) {
      breaks.push({ kind: "gap", at: row.at });
    }
  }
  if (stoppedAt !== null) breaks.push({ kind: "since", from: stoppedAt });
  return breaks.reverse();
}

/** Activity, from a menu: the page, with this screen recorded as where Back goes. */
export function openActivity(listId?: string, itemId?: string): void {
  navigate(activityPath(listId, itemId), { opener: true });
}

